import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GroupProps, InputProps } from 'antd/es/input';
import Input from 'antd/lib/input';
import Tooltip from 'antd/lib/tooltip';

import { translate } from '@translations/Translater';

import { globalMessages } from '@components/messages';
import Text from '@components/Text';
import { FontFamilyVariant, TextType } from '@components/Text';

import compactAreas from './countries.json';
import AreaSelect, { AreaSelectProps } from './CountryCodeSelect';
import { Area } from './CountryTypes';
import './styles.less';

export interface CountryPhoneInputProps
    extends Omit<InputProps, 'defaultValue' | 'value' | 'onChange'> {
    onChange?: (value: CountryPhoneInputValue) => void;
    value?: CountryPhoneInputValue;
    defaultValue?: CountryPhoneInputValue;
    selectProps?: AreaSelectProps;
    inputGroupProps?: GroupProps;
    inline?: boolean;
    popUpContainerId: string;
    error?: string;
    isPrivate?:  boolean;
    emptyResult?: string;
    isCountryCodeDisabled?: boolean;
}

export type CountryPhoneInputValue = {
    code?: number;
    phone?: string;
    short?: string;
};

function CountryCodePhoneNumber({
  defaultValue,
  onChange,
  selectProps = {},
  inputGroupProps,
  inline,
  disabled,
  popUpContainerId,
  isPrivate,
  error,
  emptyResult,
  isCountryCodeDisabled,
  ...inputProps
}: CountryPhoneInputProps) {
  if (defaultValue) {
    defaultValue.short = defaultValue.short?.toUpperCase();
  }
  if (inputProps.value) {
    inputProps.value.short = inputProps.value.short?.toUpperCase();
  }

  const defaultArea: Area | undefined = compactAreas.find((area) => {
    if (defaultValue) {
      return area.short === defaultValue.short;
    }
    return area.short === 'CN';
  });
  const [area, setArea] = useState<Area | undefined>(defaultArea);
  const [phone, setPhone] = useState<string | undefined>(defaultValue?.phone);
  const { t } = useTranslation();
  const latestInputPropsRef = useRef(inputProps);
  useEffect(() => {
    latestInputPropsRef.current = inputProps;
  }, [inputProps]);
  useEffect(() => {
    if (!('value' in latestInputPropsRef.current)) {
      return;
    }

    const {value} = inputProps;
    if (value === undefined) {
      setArea(undefined);
      setPhone(undefined);
      return;
    }
    if (value.short) {
      setArea(compactAreas.find((area) => area.short === value.short));
    } else {
      setArea(compactAreas.find((area) => area.phoneCode === value.code));
    }
    setPhone(value.phone);
  }, [inputProps.value]);

  const triggerChange = useCallback(
    (phone?: string, area?: Area) => {
      const result: CountryPhoneInputValue = {
        phone,
        code: area?.phoneCode,
        short: area?.short,
      };
            onChange?.(result);
    },
    [onChange]
  );

  const handleAreaChange = useCallback(
    (value: string) => {
      const area = compactAreas.find((area) => area.short === value);
      if (!area) {
        return;
      }
      setArea(area);
      triggerChange(phone, area);
    },
    [setArea, triggerChange, phone]
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentValue = e.target.value;
      setPhone(currentValue);
      triggerChange(currentValue, area);
    },
    [setPhone, area, triggerChange]
  );

  const areaSelect = (
    <AreaSelect
      {...selectProps}
      value={area?.short}
      onChange={handleAreaChange}
      disabled={isCountryCodeDisabled}
      getPopupContainer={() => document.getElementById(popUpContainerId)}
      emptyResult={emptyResult}
    />
  );

  if (inline) {
    inputProps.addonBefore = areaSelect;
  } else {
    inputProps.prefix = areaSelect;
  }

  if(isPrivate){
    inputProps.suffix = (
      <Tooltip
        placement="left"
        color="#f9602c"
        title={'wontShow'}
        trigger={['hover', 'click']}
      >
        <div className="private" />
      </Tooltip>
    )
  }

  return (
    <div>
      <Input
        {...inputProps}
        className="wf_country_phone_input"
        value={inputProps.value?.phone}
        onChange={handlePhoneChange}
        type="number"
        disabled={disabled}
      />
      {
        error && <Text className={`validation-error`} textType={TextType.paragraph1} fontFamilyVariant={FontFamilyVariant.light} text={error} />
      }
    </div>
  );
}

export default CountryCodePhoneNumber;

